/**
 * usePersona — CRUD hook for Persona management + active persona state.
 * Pattern follows useGenerationSessions.ts.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import type { Persona, PersonaWizardData, PersonaIdentity } from '@/types/persona.types';
import { buildBandIdentityBlock } from '@/utils/personaBuilder';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

const STORAGE_KEY = 'hookhouse_active_persona_id';

interface UsePersonaReturn {
  personas: Persona[];
  activePersona: Persona | null;
  activePersonaId: string | null;
  isLoading: boolean;
  error: string | null;
  lastError: React.RefObject<string | null>;
  fetchPersonas: () => Promise<void>;
  createPersona: (data: PersonaWizardData) => Promise<Persona | null>;
  updatePersonaFromWizard: (id: string, data: PersonaWizardData) => Promise<Persona | null>;
  updatePersona: (id: string, updates: Partial<Persona>) => Promise<void>;
  deletePersona: (id: string) => Promise<void>;
  setActivePersonaId: (id: string | null) => void;
}

export function usePersona(): UsePersonaReturn {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [activePersonaId, setActivePersonaIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setErrorState] = useState<string | null>(null);
  const lastErrorRef = useRef<string | null>(null);
  const setError = useCallback((msg: string | null) => {
    lastErrorRef.current = msg;
    setErrorState(msg);
  }, []);

  const setActivePersonaId = useCallback((id: string | null) => {
    setActivePersonaIdState(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage not available
    }
  }, []);

  const fetchPersonas = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf('/api/personas');
      if (!res.ok) throw new Error(`Failed to fetch personas (${res.status})`);
      const data = await res.json();
      setPersonas(data.personas || []);
    } catch (err: unknown) {
      console.error('🎭 Fetch personas error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createPersona = useCallback(async (wizardData: PersonaWizardData): Promise<Persona | null> => {
    setError(null);
    try {
      const identity: PersonaIdentity = wizardData.identity || {
        source: 'manual',
        name: wizardData.name,
        genre: '',
        bandBible: null,
        markdownIdentity: null,
        styleTags: '',
        excludeTags: '',
      };

      const bandIdentityBlock = buildBandIdentityBlock(identity);

      const body = {
        name: wizardData.name,
        description: wizardData.description,
        icon: wizardData.icon || '🎭',
        tags: wizardData.tags,
        identityJson: JSON.stringify(identity),
        vocalConfigJson: wizardData.vocalConfigJson || '{}',
        instrumentConfigJson: wizardData.instrumentConfigJson || 'null',
        vocalArchitecture: wizardData.vocalArchitecture || '',
        productionArchitecture: wizardData.productionArchitecture || '',
        bandIdentityBlock,
        identitySource: identity.source,
        identityName: identity.name,
        genre: identity.genre || '',
      };

      const res = await fetchWithCsrf('/api/personas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Create failed (${res.status})`);
      }
      const persona: Persona = await res.json();
      setPersonas(prev => [persona, ...prev]);
      return persona;
    } catch (err: unknown) {
      console.error('🎭 Create persona error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    }
  }, []);

  const updatePersonaFromWizard = useCallback(async (id: string, wizardData: PersonaWizardData): Promise<Persona | null> => {
    setError(null);
    try {
      const identity: PersonaIdentity = wizardData.identity || {
        source: 'manual',
        name: wizardData.name,
        genre: '',
        bandBible: null,
        markdownIdentity: null,
        styleTags: '',
        excludeTags: '',
      };

      const bandIdentityBlock = buildBandIdentityBlock(identity);

      const body = {
        name: wizardData.name,
        description: wizardData.description,
        icon: wizardData.icon || '🎭',
        tags: wizardData.tags,
        identityJson: JSON.stringify(identity),
        vocalConfigJson: wizardData.vocalConfigJson || '{}',
        instrumentConfigJson: wizardData.instrumentConfigJson || 'null',
        vocalArchitecture: wizardData.vocalArchitecture || '',
        productionArchitecture: wizardData.productionArchitecture || '',
        bandIdentityBlock,
        identitySource: identity.source,
        identityName: identity.name,
        genre: identity.genre || '',
      };

      const res = await fetchWithCsrf(`/api/personas/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Update failed (${res.status})`);
      }
      const updated: Persona = await res.json();
      setPersonas(prev => prev.map(p => p.id === id ? updated : p));
      return updated;
    } catch (err: unknown) {
      console.error('🎭 Update persona from wizard error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    }
  }, []);

  const updatePersona = useCallback(async (id: string, updates: Partial<Persona>) => {
    setError(null);
    try {
      const res = await fetchWithCsrf(`/api/personas/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(`Update failed (${res.status})`);
      const updated: Persona = await res.json();
      setPersonas(prev => prev.map(p => p.id === id ? updated : p));
    } catch (err: unknown) {
      console.error('🎭 Update persona error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, []);

  const deletePersona = useCallback(async (id: string) => {
    setError(null);
    try {
      const res = await fetchWithCsrf(`/api/personas/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setPersonas(prev => prev.filter(p => p.id !== id));
      if (activePersonaId === id) setActivePersonaId(null);
    } catch (err: unknown) {
      console.error('🎭 Delete persona error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }, [activePersonaId, setActivePersonaId]);

  // Fetch on mount
  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  const activePersona = personas.find(p => p.id === activePersonaId) || null;

  return {
    personas,
    activePersona,
    activePersonaId,
    isLoading,
    error,
    lastError: lastErrorRef,
    fetchPersonas,
    createPersona,
    updatePersonaFromWizard,
    updatePersona,
    deletePersona,
    setActivePersonaId,
  };
}
